import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FlaskConical, Code, Lock, Eye, EyeOff, Server } from 'lucide-react';
import { SDKEnquiryForm } from '../components/forms/SDKEnquiryForm';

const SailPage = () => {
  const [showSDKForm, setShowSDKForm] = useState(false);

  return (
    <div data-testid="sail-page">
      {/* Opening */}
      <section className="bg-slate-900 text-white py-16 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-white mb-8">
              SAIL
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              SAIL is the implementation layer of the SAFE standard.
            </p>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              It operationalises safety within AI systems.
            </p>
            <p className="text-lg md:text-xl text-slate-300 mb-4">
              It does not define the standard.
            </p>
            <p className="text-lg md:text-xl text-slate-300">
              It does not verify compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Structural Separation */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">Structural Separation</p>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">SAFE defines what safety is.</p>
              <p className="text-lg text-slate-700">SAFE Labs verifies whether systems meet it.</p>
              <p className="text-lg text-slate-700">SAIL implements safety within systems.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold mt-8 mb-2">
              These roles are separate by design.
            </p>
            <p className="text-lg text-slate-900 font-semibold">
              They cannot be combined.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            <div className="bg-slate-900 text-white p-8 border-l-4 border-slate-900">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE</h3>
              <p className="text-slate-300">Defines the standard</p>
            </div>
            <div className="bg-cyan-700 text-white p-8 border-l-4 border-cyan-700">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <FlaskConical className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAFE Labs</h3>
              <p className="text-cyan-100">Verifies systems independently</p>
            </div>
            <div className="bg-amber-600 text-white p-8 border-l-4 border-amber-600">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-6">
                <Code className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SAIL</h3>
              <p className="text-amber-100">Implements safety within systems</p>
            </div>
          </div>
        </div>
      </section>

      {/* Function */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Function</p>
            <p className="text-lg text-slate-700 mb-6">
              SAIL enables systems to operate within the SAFE standard.
            </p>
            <p className="text-lg text-slate-700 mb-6">It supports:</p>
            <div className="space-y-3">
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Detection of defined risk patterns</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Responses aligned to SAFE thresholds</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Integration of human escalation pathways</p>
              </div>
              <div className="border-l-4 border-slate-900 pl-6">
                <p className="text-lg text-slate-900 font-medium">Operation within verified safety parameters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SDK Integration - Code Example */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="caption text-cyan-700 mb-4">SDK Integration</p>
              <h2 className="heading-2 mb-6">Implementation Reference</h2>
              <div className="space-y-4">
                {[
                  'Trajectory-based harm detection',
                  'Governance-defined intervention thresholds',
                  'Privacy-preserving safeguarding signals',
                  'Audit-safe intervention records'
                ].map((item, index) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-amber-600"></div>
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-900 p-8">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-4 font-mono">Example Integration</p>
              <pre className="text-sm font-mono leading-relaxed text-slate-300 overflow-x-auto">
<span className="text-cyan-400">import</span> {'{'} SafeGuard {'}'} <span className="text-cyan-400">from</span> <span className="text-amber-400">'@safe/sail-sdk'</span>;{'\n'}
{'\n'}
<span className="text-slate-500">// Initialize with governance rules</span>{'\n'}
<span className="text-cyan-400">const</span> guard = <span className="text-cyan-400">new</span> <span className="text-emerald-400">SafeGuard</span>({'{'}
  rules: <span className="text-amber-400">'SAFE-001-v1'</span>,
  mode: <span className="text-amber-400">'on-device'</span>
{'}'});{'\n'}
{'\n'}
<span className="text-slate-500">// Process conversation trajectory</span>{'\n'}
<span className="text-cyan-400">const</span> result = <span className="text-cyan-400">await</span> guard.<span className="text-emerald-400">evaluate</span>(
  conversation
);{'\n'}
{'\n'}
<span className="text-cyan-400">if</span> (result.intervention_tier {'>'} <span className="text-emerald-400">0</span>) {'{'}
  <span className="text-slate-500">// Trigger governance-defined response</span>
  <span className="text-cyan-400">await</span> guard.<span className="text-emerald-400">respond</span>(result);
{'}'}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Audit Record Structure */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">Audit Trail</p>
            <h2 className="heading-2 mb-6">Privacy-Preserving Records</h2>
            <p className="text-lg text-slate-700">
              SAIL audit records contain only the minimum information required for governance verification. No message content is stored.
            </p>
          </div>

          <div className="bg-slate-900 text-white p-8 max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4 font-mono">Audit Record Structure</p>
            <pre className="text-sm font-mono leading-relaxed">
              <span className="text-slate-500">{'{'}</span>{'\n'}
              <span className="text-cyan-400">  "timestamp"</span>: <span className="text-amber-400">"2025-01-15T14:32:00Z"</span>,{'\n'}
              <span className="text-cyan-400">  "confidence_score"</span>: <span className="text-emerald-400">0.73</span>,{'\n'}
              <span className="text-cyan-400">  "intervention_tier"</span>: <span className="text-emerald-400">2</span>,{'\n'}
              <span className="text-cyan-400">  "rule_triggered"</span>: <span className="text-amber-400">"boundary_erosion_v1"</span>{'\n'}
              <span className="text-slate-500">{'}'}</span>
            </pre>
          </div>
        </div>
      </section>

      {/* Constraint */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-400 mb-4">Constraint</p>
            <p className="text-lg text-slate-300 mb-4">
              SAIL cannot certify itself.
            </p>
            <p className="text-lg text-white font-semibold">
              All implementations must be independently verified through SAFE Labs.
            </p>
          </div>
        </div>
      </section>

      {/* MVP Examples */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mb-12">
            <p className="caption text-cyan-700 mb-4">In Practice</p>
            <p className="text-lg text-slate-700 mb-4">
              SAIL operates in real-time within messaging environments.
            </p>
            <p className="text-lg text-slate-700">
              Below are examples of SAIL interventions from the working MVP.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="border border-slate-200 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/9ladui99_SAIL%20intervention%20flagging.png"
                  alt="SAIL Layer 4 intervention — escalation detection and user prompt"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-900">Escalation Detection</p>
                <p className="text-xs text-slate-600 mt-1">
                  SAIL detects rapid escalation from location sharing to video requests. Intervention is delivered directly to the user with the option to disengage.
                </p>
              </div>
            </div>
            <div>
              <div className="border border-slate-200 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/8gfyza4x_Innocent%20teens%20chat%20flagged.png"
                  alt="SAIL message flagging — inappropriate content detected and confirmed"
                  className="w-full h-auto"
                />
              </div>
              <div className="mt-4">
                <p className="text-sm font-semibold text-slate-900">Message Flagging</p>
                <p className="text-xs text-slate-600 mt-1">
                  SAIL flags content confirmed as inappropriate by the receiver. This applies to all users — not only predatory behaviour but also conversations between peers where requests may cross safety thresholds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Position */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="caption text-cyan-700 mb-4">Position</p>
            <div className="space-y-4">
              <p className="text-lg text-slate-700">Any system can implement the SAFE standard.</p>
              <p className="text-lg text-slate-700">SAIL is one implementation aligned to it.</p>
            </div>
            <p className="text-lg text-slate-900 font-semibold mt-8">
              Implementation does not equal verification.
            </p>
          </div>
        </div>
      </section>

      {/* Final Line + CTAs */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <p className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-12">
            Implementation is not proof.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/labs"
              className="btn-primary"
              data-testid="btn-learn-certification"
            >
              Learn About Certification
            </Link>
            <button
              onClick={() => setShowSDKForm(true)}
              className="btn-secondary"
              data-testid="btn-sdk-enquiry"
            >
              SDK Integration Enquiry
            </button>
          </div>
        </div>
      </section>

      <SDKEnquiryForm
        isOpen={showSDKForm}
        onClose={() => setShowSDKForm(false)}
      />
    </div>
  );
};

export default SailPage;
